import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { institutesApi, type GetInstitutesParams } from '../api/endpoints/institutes';
import toast from 'react-hot-toast';

export function useInstitutes() {
  const queryClient = useQueryClient();

  const useList = (params: GetInstitutesParams) =>
    useQuery({
      queryKey: ['admin-institutes', params],
      queryFn: () => institutesApi.getList(params).then((r) => r.data)
    });

  const useDetail = (id: number | null) =>
    useQuery({
      queryKey: ['admin-institute', id],
      queryFn: () => institutesApi.getDetail(id!).then((r) => r.data.data),
      enabled: !!id,
    });

  const useActivate = () =>
    useMutation({
      mutationFn: ({
        id,
        activate,
        reason,
      }: {
        id: number;
        activate: boolean;
        reason?: string;
      }) => institutesApi.activate(id, activate, reason),
      onSuccess: (_, { activate }) => {
        toast.success(`Institute ${activate ? 'activated' : 'deactivated'} successfully`);
        queryClient.invalidateQueries({ queryKey: ['admin-institutes'] });
        queryClient.invalidateQueries({ queryKey: ['admin-institute'] });
      },
      onError: () => toast.error('Failed to update institute status'),
    });

  const useFeatureFlags = (id: number | null) =>
    useQuery({
      queryKey: ['admin-institute-feature-flags', id],
      queryFn: () => institutesApi.getFeatureFlags(id!).then((r) => r.data.data ?? {}),
      enabled: !!id,
    });

  const useSetFeatureFlag = (id: number) =>
    useMutation({
      mutationFn: ({ featureKey, isEnabled }: { featureKey: string; isEnabled: boolean }) =>
        institutesApi.setFeatureFlag(id, featureKey, isEnabled),
      onSuccess: (_, { featureKey, isEnabled }) => {
        toast.success(`Feature "${featureKey}" ${isEnabled ? 'enabled' : 'disabled'}`);
        queryClient.invalidateQueries({ queryKey: ['admin-institute-feature-flags', id] });
      },
      onError: () => toast.error('Failed to update feature flag'),
    });

  const useSystemConfigs = () =>
    useQuery({
      queryKey: ['admin-system-configs'],
      queryFn: () => institutesApi.getSystemConfigs().then((r) => r.data.data)
    });

  const useUpdateSystemConfig = () =>
    useMutation({
      mutationFn: ({ key, value }: { key: string; value: string }) =>
        institutesApi.updateSystemConfig(key, value),
      onSuccess: () => {
        toast.success('Configuration updated');
        queryClient.invalidateQueries({ queryKey: ['admin-system-configs'] });
      },
      onError: () => toast.error('Failed to update configuration'),
    });

  return {
    useList,
    useDetail,
    useActivate,
    useFeatureFlags,
    useSetFeatureFlag,
    useSystemConfigs,
    useUpdateSystemConfig,
  };
}
